import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

const JobResult = ({ job, isDarkMode, isGuest = false }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const navigate = useNavigate();

  if (!job) return null;

  const displayJob = {
  title: job.title || "Untitled Job",
  company: job.company || "Unknown Company", // ✅ fixed key
  location: job.location || "Location not specified",
  salary: job.salary || "Not disclosed",
  type: job.job_type || "N/A",
  posted: job.posted_on || "Unknown", // ✅ fixed key
  apply_url: job.link || job.url || "#",
  description: job.description || "No description available",
};


  const toggleDetails = () => setShowDetails(!showDetails);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -15 }}
        transition={{ duration: 0.3 }}
        className={`relative overflow-hidden rounded-2xl shadow-lg transition-all duration-300 hover:shadow-2xl ${
          isDarkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"
        }`}
      >
        {/* Left border */}
        <div
          className={`absolute left-0 top-0 h-full w-1 ${
            isDarkMode ? "bg-gray-600" : "bg-gray-300"
          }`}
        />

        <div className="p-6">
          {/* Title */}
          <h3
            onClick={toggleDetails}
            className={`cursor-pointer text-xl font-semibold transition-colors ${
              isDarkMode
                ? "text-white hover:text-gray-300"
                : "text-gray-900 hover:text-gray-700"
            }`}
          >
            {displayJob.title}
          </h3>

          {/* Company + location */}
          <p
            className={`mt-1 text-sm ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            {displayJob.company} • {displayJob.location}
          </p>

          {/* Tags */}
          <div className="mt-3 flex flex-wrap gap-2">
            <span
              className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium ${
                isDarkMode
                  ? "bg-gray-800 text-gray-200"
                  : "bg-gray-200 text-gray-700"
              }`}
            >
              💼 {displayJob.type}
            </span>
            <span
              className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium ${
                isDarkMode
                  ? "bg-gray-800 text-gray-200"
                  : "bg-gray-200 text-gray-700"
              }`}
            >
              💰 {displayJob.salary}
            </span>
            <span
              className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium ${
                isDarkMode
                  ? "bg-gray-800 text-gray-200"
                  : "bg-gray-200 text-gray-700"
              }`}
            >
              📅 {displayJob.posted}
            </span>
          </div>

          {/* Description */}
          <AnimatePresence>
            {showDetails && (
              <motion.p
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className={`mt-4 text-sm leading-relaxed ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                {displayJob.description}
              </motion.p>
            )}
          </AnimatePresence>

          {/* Footer buttons */}
          <div className="mt-6 flex items-center justify-between">
            {/* Apply Now */}
            {isGuest ? (
              <motion.button
                onClick={() => setShowSignupModal(true)}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                  isDarkMode
                    ? "bg-gray-800 text-white"
                    : "bg-gray-700 text-white"
                } hover:shadow-lg`}
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
                  />
                </svg>
                Apply Now
              </motion.button>
            ) : (
              <motion.a
                href={displayJob.apply_url}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                  isDarkMode
                    ? "bg-gray-800 text-white"
                    : "bg-gray-700 text-white"
                } hover:shadow-lg`}
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
                  />
                </svg>
                Apply Now
              </motion.a>
            )}

            {/* Loader */}
            {loading && (
              <div className="flex items-center">
                <svg
                  className="animate-spin h-5 w-5 mr-2 text-gray-400"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8z"
                  ></path>
                </svg>
                <span className="text-sm text-gray-400">Loading...</span>
              </div>
            )}
          </div>
        </div>

        {/* Signup Modal for Guest Users */}
        <AnimatePresence>
          {showSignupModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
              onClick={() => setShowSignupModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className={`relative max-w-md w-full mx-4 p-6 rounded-2xl shadow-xl ${
                  isDarkMode ? "bg-[#1C1C1C] text-white" : "bg-white text-gray-900"
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => setShowSignupModal(false)}
                  className={`absolute top-4 right-4 p-1 rounded-full transition-colors ${
                    isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                <div className="text-center">
                  <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                    isDarkMode ? "bg-gray-800" : "bg-gray-100"
                  }`}>
                    <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>

                  <h3 className="text-xl font-bold mb-2">Sign Up to Apply</h3>
                  <p className={`mb-6 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                    Create a free account to apply for this job and unlock all features.
                  </p>

                  <div className="flex flex-col gap-3">
                    <motion.button
                      onClick={() => navigate("/signup")}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full py-3 px-4 rounded-lg font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                    >
                      Sign Up
                    </motion.button>
                    <motion.button
                      onClick={() => navigate("/login")}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
                        isDarkMode
                          ? "bg-gray-800 text-white hover:bg-gray-700"
                          : "bg-gray-200 text-gray-900 hover:bg-gray-300"
                      }`}
                    >
                      Already have an account? Sign In
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
};

export default JobResult;
